import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../api/agentsApi';
import type {
  CreateSkillInput,
  DeleteSkillInput,
  SuggestCustomInstructionsInput,
  ToggleSkillInput,
  UpdateCustomInstructionsInput,
  UpdateSkillInput,
} from '../types';
import { AGENT_KEYS } from './useAgents';

export const SKILL_KEYS = {
  skills: (agentType?: string) => [...AGENT_KEYS.all, 'skills', agentType] as const,
  orgSkills: (orgId: string, agentType: string) =>
    [...AGENT_KEYS.all, 'org-skills', orgId, agentType] as const,
  promptPreview: (orgId: string, agentType: string) =>
    [...AGENT_KEYS.all, 'prompt-preview', orgId, agentType] as const,
};

export function useAgentSkills(agentType?: string) {
  return useQuery({
    queryKey: SKILL_KEYS.skills(agentType),
    queryFn: () => agentsApi.getSkills(agentType),
    staleTime: 5 * 60_000,
  });
}

export function useOrgAgentSkills(organizationId: string, agentType: string) {
  return useQuery({
    queryKey: SKILL_KEYS.orgSkills(organizationId, agentType),
    queryFn: () => agentsApi.getOrgSkills(organizationId, agentType),
    enabled: !!organizationId && !!agentType,
    staleTime: 5 * 60_000,
  });
}

export function useToggleSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ToggleSkillInput) => agentsApi.toggleSkill(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'org-skills', variables.organizationId],
      });
    },
  });
}

export function useUpdateCustomInstructions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCustomInstructionsInput) => agentsApi.updateCustomInstructions(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.config(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'prompt-preview', variables.organizationId],
      });
    },
  });
}

// Read-only: never invalidates any query cache. Suggestion generation never
// touches `AgentConfiguration.customPrompts` — persistence happens only via
// `useUpdateCustomInstructions` when the caller explicitly Saves.
export function useSuggestCustomInstructions() {
  return useMutation({
    mutationFn: (input: SuggestCustomInstructionsInput) =>
      agentsApi.suggestCustomInstructions(input),
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSkillInput) => agentsApi.createSkill(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'org-skills', variables.organizationId],
      });
    },
  });
}

export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSkillInput) => agentsApi.updateSkill(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'org-skills', variables.organizationId],
      });
    },
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: DeleteSkillInput) => agentsApi.deleteSkill(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'org-skills', variables.organizationId],
      });
    },
  });
}

export function usePromptPreview(organizationId: string, agentType: string, enabled = false) {
  return useQuery({
    queryKey: SKILL_KEYS.promptPreview(organizationId, agentType),
    queryFn: () => agentsApi.getPromptPreview(organizationId, agentType),
    enabled: enabled && !!organizationId && !!agentType,
    staleTime: 5 * 60_000,
  });
}
